import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import PhotoUploader from "../components/PhotoUploader";
import DefectsList from "./DefectsList";
import { toast } from "sonner";
import { Archive, ClipboardCheck, NotebookPen, Trash2, FileText, ChevronDown, ChevronRight, Plus, CalendarCheck } from "lucide-react";

const TYPE_LABEL = {
  maintenance: "Renhold",
  checkin: "Innsjekk",
  checkout: "Utsjekk",
};

const CATEGORY_LABEL = {
  service: "Service",
  repair: "Reparasjon",
  observation: "Observasjon",
  other: "Annet",
};

function nb(s) {
  return new Date(s).toLocaleString("nb-NO", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ISO week number, Monday-based (Norway standard)
function isoWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return { week: Math.ceil(((date - yearStart) / 86400000 + 1) / 7), year: date.getUTCFullYear() };
}

const MONTHS_NO = ["Januar", "Februar", "Mars", "April", "Mai", "Juni",
                   "Juli", "August", "September", "Oktober", "November", "Desember"];

/**
 * Builds a nested tree: { year-month-key: { label, weeks: { week-key: { label, items } } } }
 * Items must have a `_date` (Date) attribute used for grouping.
 */
function groupByMonthWeek(items) {
  const map = new Map();
  items.forEach((it) => {
    const d = it._date;
    const ymKey = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
    const ymLabel = `${MONTHS_NO[d.getMonth()]} ${d.getFullYear()}`;
    const { week } = isoWeek(d);
    const wKey = `${ymKey}-w${week}`;
    if (!map.has(ymKey)) map.set(ymKey, { label: ymLabel, sort: ymKey, weeks: new Map() });
    const monthBucket = map.get(ymKey);
    if (!monthBucket.weeks.has(wKey)) {
      monthBucket.weeks.set(wKey, { label: `Uke ${week}`, sort: wKey, items: [] });
    }
    monthBucket.weeks.get(wKey).items.push(it);
  });
  // Sort newest first
  return [...map.values()]
    .sort((a, b) => (a.sort < b.sort ? 1 : -1))
    .map((m) => ({
      ...m,
      weeks: [...m.weeks.values()].sort((a, b) => (a.sort < b.sort ? 1 : -1)),
    }));
}

function GroupedList({ items, renderItem, emptyText, testIdPrefix }) {
  const grouped = useMemo(() => groupByMonthWeek(items), [items]);
  const [openMonths, setOpenMonths] = useState(() =>
    grouped.length ? { [grouped[0].sort]: true } : {},
  );
  const [openWeeks, setOpenWeeks] = useState(() =>
    grouped.length && grouped[0].weeks.length ? { [grouped[0].weeks[0].sort]: true } : {},
  );

  if (!items.length) {
    return (
      <Card className="p-8 text-center" data-testid={`${testIdPrefix}-empty`}>
        <Archive className="w-10 h-10 text-[#BD984D] mx-auto mb-3" />
        <p className="text-[#2B3F4D]">{emptyText}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {grouped.map((m) => {
        const monthOpen = openMonths[m.sort] !== false;
        const totalCount = m.weeks.reduce((acc, w) => acc + w.items.length, 0);
        return (
          <div key={m.sort} data-testid={`${testIdPrefix}-month-${m.sort}`}>
            <button
              type="button"
              onClick={() => setOpenMonths((p) => ({ ...p, [m.sort]: !monthOpen }))}
              className="w-full flex items-center gap-2 text-left mb-2"
              data-testid={`${testIdPrefix}-month-toggle-${m.sort}`}
            >
              {monthOpen ? (
                <ChevronDown className="w-4 h-4 text-[#A57F39]" />
              ) : (
                <ChevronRight className="w-4 h-4 text-[#A57F39]" />
              )}
              <span className="text-xs uppercase tracking-[0.32em] text-[#A57F39] font-bold">
                {m.label}
              </span>
              <Badge className="bg-[#BD984D]/15 text-[#A57F39] border-0 ml-1">
                {totalCount}
              </Badge>
            </button>

            {monthOpen && (
              <div className="space-y-3 pl-6 border-l-2 border-[#BD984D]/25">
                {m.weeks.map((w) => {
                  const wOpen = openWeeks[w.sort] !== false;
                  return (
                    <div key={w.sort} data-testid={`${testIdPrefix}-week-${w.sort}`}>
                      <button
                        type="button"
                        onClick={() => setOpenWeeks((p) => ({ ...p, [w.sort]: !wOpen }))}
                        className="w-full flex items-center gap-2 text-left mb-2"
                        data-testid={`${testIdPrefix}-week-toggle-${w.sort}`}
                      >
                        {wOpen ? (
                          <ChevronDown className="w-3.5 h-3.5 text-[#1DA3B3]" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-[#1DA3B3]" />
                        )}
                        <span className="text-sm font-semibold text-[#0C1824]">{w.label}</span>
                        <span className="text-xs text-[#2B3F4D]">({w.items.length})</span>
                      </button>
                      {wOpen && (
                        <div className="space-y-2 pl-5">
                          {w.items.map((it) => renderItem(it))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Sjekklister
// ---------------------------------------------------------------------------
function ChecklistsArchive({ boats }) {
  const [items, setItems] = useState([]);
  const [boatFilter, setBoatFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const params = { status: "completed" };
    if (boatFilter !== "all") params.boat_id = boatFilter;
    if (typeFilter !== "all") params.type = typeFilter;
    api.get("/checklists", { params }).then((r) => {
      const decorated = r.data.map((c) => ({
        ...c,
        _date: new Date(c.completed_at || c.created_at),
      }));
      setItems(decorated);
      setLoaded(true);
    });
  }, [boatFilter, typeFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-[#A57F39]">Båt</Label>
          <Select value={boatFilter} onValueChange={setBoatFilter}>
            <SelectTrigger className="w-[200px]" data-testid="archive-cl-filter-boat">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle båter</SelectItem>
              {boats.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-[#A57F39]">Type</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="archive-cl-filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle typer</SelectItem>
              <SelectItem value="maintenance">Renhold</SelectItem>
              <SelectItem value="checkin">Innsjekk</SelectItem>
              <SelectItem value="checkout">Utsjekk</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loaded && (
        <GroupedList
          items={items}
          testIdPrefix="archive-cl"
          emptyText="Ingen fullførte sjekklister i arkivet ennå."
          renderItem={(cl) => {
            const issues = (cl.responses || []).filter((r) => r.status === "not_ok").length;
            return (
              <Card key={cl.id} className="p-4 hover:border-[#BD984D] transition-colors"
                    data-testid={`archive-cl-row-${cl.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-[#0C1824] text-white border-0">
                        {TYPE_LABEL[cl.type] || cl.type}
                      </Badge>
                      <span className="font-semibold text-[#0C1824]">
                        {(boats.find((b) => b.id === cl.boat_id) || {}).name || cl.boat_id}
                      </span>
                      {issues > 0 && (
                        <Badge className="bg-red-100 text-red-700 border-0">{issues} avvik</Badge>
                      )}
                    </div>
                    <div className="text-xs text-[#2B3F4D] mt-1.5">
                      Fullført {nb(cl.completed_at || cl.created_at)} · av {cl.performed_by_name || "—"}
                    </div>
                    {cl.overall_note && (
                      <div className="text-sm text-[#0C1824] mt-2 italic">«{cl.overall_note}»</div>
                    )}
                  </div>
                  <Link to={`/checklist/${cl.id}`}>
                    <Button variant="outline" size="sm" data-testid={`archive-cl-open-${cl.id}`}
                            className="border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10">
                      <FileText className="w-3.5 h-3.5 mr-1.5" /> Åpne
                    </Button>
                  </Link>
                </div>
              </Card>
            );
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Notater
// ---------------------------------------------------------------------------
function NotesArchive({ boats }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [boatFilter, setBoatFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    boat_id: "", title: "", body: "", category: "service", photo_ids: [],
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = {};
    if (boatFilter !== "all") params.boat_id = boatFilter;
    if (categoryFilter !== "all") params.category = categoryFilter;
    const r = await api.get("/service-notes", { params });
    setItems(
      r.data.map((n) => ({ ...n, _date: new Date(n.performed_at || n.created_at) })),
    );
  };

  useEffect(() => {
    const params = {};
    if (boatFilter !== "all") params.boat_id = boatFilter;
    if (categoryFilter !== "all") params.category = categoryFilter;
    api.get("/service-notes", { params }).then((r) =>
      setItems(r.data.map((n) => ({ ...n, _date: new Date(n.performed_at || n.created_at) }))),
    );
  }, [boatFilter, categoryFilter]);

  const submit = async () => {
    if (!form.boat_id || !form.body.trim()) {
      toast.error("Velg båt og skriv inn notattekst.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/service-notes", { ...form, body: form.body.trim(), title: form.title.trim() });
      toast.success("Notat arkivert");
      setOpen(false);
      setForm({ boat_id: "", title: "", body: "", category: "service", photo_ids: [] });
      await load();
    } catch (e) {
      toast.error("Kunne ikke lagre notat");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slette dette notatet permanent?")) return;
    await api.delete(`/service-notes/${id}`);
    toast.success("Notat slettet");
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#A57F39]">Båt</Label>
            <Select value={boatFilter} onValueChange={setBoatFilter}>
              <SelectTrigger className="w-[200px]" data-testid="archive-note-filter-boat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle båter</SelectItem>
                {boats.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#A57F39]">Kategori</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]" data-testid="archive-note-filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle kategorier</SelectItem>
                {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white"
                    data-testid="archive-note-new">
              <Plus className="w-4 h-4 mr-1.5" /> Nytt notat
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Legg igjen notat i arkivet</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Båt</Label>
                <Select value={form.boat_id} onValueChange={(v) => setForm({ ...form, boat_id: v })}>
                  <SelectTrigger data-testid="archive-note-form-boat">
                    <SelectValue placeholder="Velg båt" />
                  </SelectTrigger>
                  <SelectContent>
                    {boats.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="archive-note-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tittel (valgfritt)</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Kort tittel, f.eks. «Byttet thruster-sikring»"
                  data-testid="archive-note-form-title"
                />
              </div>
              <div>
                <Label>Notat</Label>
                <Textarea
                  rows={5}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Beskriv jobben som ble utført, observasjoner eller annen relevant info."
                  data-testid="archive-note-form-body"
                />
              </div>
              <div>
                <Label>Bilder</Label>
                <PhotoUploader
                  photoIds={form.photo_ids}
                  onChange={(ids) => setForm({ ...form, photo_ids: ids })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Avbryt</Button>
              <Button onClick={submit} disabled={saving}
                      className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white"
                      data-testid="archive-note-form-submit">
                {saving ? "Lagrer…" : "Arkiver notat"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <GroupedList
        items={items}
        testIdPrefix="archive-note"
        emptyText="Ingen notater arkivert ennå. Trykk «Nytt notat» for å legge til."
        renderItem={(n) => (
          <Card key={n.id} className="p-4" data-testid={`archive-note-row-${n.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3] border-0">
                    {CATEGORY_LABEL[n.category] || n.category}
                  </Badge>
                  <span className="font-semibold text-[#0C1824]">{n.boat_name}</span>
                  {n.title && <span className="text-sm text-[#2B3F4D]">— {n.title}</span>}
                </div>
                <div className="text-xs text-[#2B3F4D] mt-1.5">
                  {nb(n.performed_at)} · av {n.author_name} ({n.author_role})
                </div>
                <div className="text-sm text-[#0C1824] mt-2 whitespace-pre-wrap">{n.body}</div>
                {n.photo_ids?.length > 0 && (
                  <div className="text-xs text-[#A57F39] mt-2">📎 {n.photo_ids.length} bilde(r)</div>
                )}
              </div>
              {user?.role === "admin" && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => remove(n.id)}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid={`archive-note-delete-${n.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </Card>
        )}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bookings archive tab – completed bookings (end_date < today)
// ---------------------------------------------------------------------------
function BookingsArchive({ boats }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [boatFilter, setBoatFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get("/bookings", { params: { only_completed: true } })
      .then((r) => setBookings(r.data || []))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  }, []);

  const boatName = (id) => boats.find((b) => b.id === id)?.name || "Ukjent båt";
  const filtered = bookings.filter((b) => {
    if (boatFilter !== "all" && b.boat_id !== boatFilter) return false;
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (b.customer_name || "").toLowerCase().includes(s)
      || (b.customer_email || "").toLowerCase().includes(s)
      || (boatName(b.boat_id) || "").toLowerCase().includes(s)
    );
  });

  // Grupper etter måned (nyeste først).
  const groups = {};
  filtered.forEach((b) => {
    const key = (b.end_date || b.start_date || "").slice(0, 7) || "ukjent";
    (groups[key] ||= []).push(b);
  });
  const sortedKeys = Object.keys(groups).sort().reverse();
  const monthLabel = (ym) => {
    if (!ym || ym === "ukjent") return "Ukjent periode";
    const [y, m] = ym.split("-");
    const names = ["januar", "februar", "mars", "april", "mai", "juni",
                   "juli", "august", "september", "oktober", "november", "desember"];
    return `${names[parseInt(m, 10) - 1]} ${y}`;
  };

  return (
    <div className="space-y-4" data-testid="bookings-archive">
      <Card className="p-3 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">Søk</Label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Søk på kunde, e-post eller båt…"
            data-testid="bookings-archive-search"
          />
        </div>
        <div className="min-w-[220px]">
          <Label className="text-xs">Båt</Label>
          <Select value={boatFilter} onValueChange={setBoatFilter}>
            <SelectTrigger data-testid="bookings-archive-boat-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle båter</SelectItem>
              {boats.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="text-sm text-[#2B3F4D] py-6 text-center">Laster…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-6 text-sm text-[#2B3F4D] italic text-center" data-testid="bookings-archive-empty">
          Ingen fullførte bookinger å vise.
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedKeys.map((key) => (
            <div key={key}>
              <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39] mb-2">
                {monthLabel(key)} · {groups[key].length}
              </div>
              <div className="space-y-2">
                {groups[key].map((b) => (
                  <Link
                    key={b.id}
                    to={`/admin/bookings/${b.id}`}
                    data-testid={`bookings-archive-row-${b.id}`}
                    className="block"
                  >
                    <Card className="p-4 hover:border-[#BD984D]/60 hover:shadow-sm transition-all cursor-pointer">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
                            {boatName(b.boat_id)}
                          </div>
                          <div className="font-semibold text-[#0C1824]">{b.customer_name}</div>
                          <div className="text-xs text-[#2B3F4D] mt-0.5">
                            {(b.start_date || "").slice(0, 10)} → {(b.end_date || "").slice(0, 10)}
                            {" · "}{b.num_guests} pers.
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function ArchivePage() {
  const [boats, setBoats] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ["checklists", "notes", "defects", "bookings"].includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "checklists";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    api.get("/boats").then((r) => setBoats(r.data));
  }, []);

  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "checklists") next.delete("tab");
    else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Arkiv</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0C1824] mt-1">Historikk</h1>
        <p className="text-sm text-[#2B3F4D] mt-2 max-w-2xl">
          Fullførte sjekklister, løste avvik og servicenotater – gruppert per måned og uke for
          rask gjenfinning.
        </p>
      </div>

      <Tabs value={tab} onValueChange={changeTab} className="w-full">
        <div className="overflow-x-auto -mx-1 pb-1">
          <TabsList className="bg-[#0C1824]/5 border border-[#BD984D]/25 w-max min-w-full">
            <TabsTrigger value="checklists" data-testid="archive-tab-checklists"
                         className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
              <ClipboardCheck className="w-4 h-4 mr-1.5" /> Sjekklister
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="archive-tab-notes"
                         className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
              <NotebookPen className="w-4 h-4 mr-1.5" /> Notater
            </TabsTrigger>
            <TabsTrigger value="defects" data-testid="archive-tab-defects"
                         className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
              <Archive className="w-4 h-4 mr-1.5" /> Avvik
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="archive-tab-bookings"
                         className="data-[state=active]:bg-[#0C1824] data-[state=active]:text-white">
              <CalendarCheck className="w-4 h-4 mr-1.5" /> Bookinger
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="checklists" className="mt-6">
          <ChecklistsArchive boats={boats} />
        </TabsContent>
        <TabsContent value="notes" className="mt-6">
          <NotesArchive boats={boats} />
        </TabsContent>
        <TabsContent value="defects" className="mt-6">
          <DefectsList archiveOnly />
        </TabsContent>
        <TabsContent value="bookings" className="mt-6">
          <BookingsArchive boats={boats} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
